/**
 * Sentry init config-driven — IR-OBS-001 (single init por app).
 *
 * Drift histórico capturado:
 * - admin/sales: SEM integrations[] (sem replay, sem browserTracing).
 * - crm/public: COM integrations[browserTracing, replay].
 *
 * Decisão: ligar integrations por DEFAULT em todos os apps (paridade) — opt-out via
 * `enableIntegrations: false`. Mantém comportamento dos apps que já tinham e
 * adiciona observabilidade nos que não tinham.
 */
import * as Sentry from "@sentry/react";
import type { InitSentryConfig } from "./types";
import { buildScrubber } from "./scrub";

const DEFAULT_IGNORED_MESSAGES = ["ResizeObserver loop", "ChunkLoadError"];

/**
 * Ruído conhecido de SDK terceiros que NÃO deve chegar ao Sentry.
 *
 * - Supabase auth client usa `navigator.locks.request(..., { steal: true })`
 *   para coordenar refresh de token entre tabs. Quando uma 2ª tab abre, a
 *   promise pendente da 1ª rejeita com `AbortError: Lock broken by another
 *   request with the 'steal' option.`. É comportamento esperado, não é bug.
 *   Issue #387.
 *
 * Importante: filtrar SÓ AbortError com essa mensagem específica. Outros
 * AbortError (fetch cancelado, etc.) e outros erros de auth continuam passando.
 */
export function shouldDropEvent(
  originalException: unknown,
  eventMessage: string,
): boolean {
  for (const ignored of DEFAULT_IGNORED_MESSAGES) {
    if (eventMessage.includes(ignored)) return true;
  }

  if (
    originalException instanceof Error &&
    originalException.name === "AbortError" &&
    typeof originalException.message === "string" &&
    originalException.message.includes("Lock broken by another request")
  ) {
    return true;
  }

  return false;
}

export function initSentry(config: InitSentryConfig): void {
  const {
    dsn,
    environment,
    app,
    release,
    enabled,
    tracesSampleRate = 0.1,
    replaysSessionSampleRate = 0,
    replaysOnErrorSampleRate = 1.0,
    enableIntegrations = true,
    enablePiiScrubbing = true,
    scrubAdditionalFields,
    beforeSend: userBeforeSend,
    sendDefaultPii = true,
  } = config;

  const isProd =
    typeof import.meta !== "undefined" &&
    typeof import.meta.env !== "undefined" &&
    import.meta.env.PROD === true;

  const effectiveEnabled = enabled ?? (isProd && !!dsn);

  if (!effectiveEnabled || !dsn) return;

  const scrub = enablePiiScrubbing
    ? buildScrubber({ additionalFields: scrubAdditionalFields })
    : null;

  const integrations = enableIntegrations
    ? [Sentry.browserTracingIntegration(), Sentry.replayIntegration()]
    : [];

  Sentry.init({
    dsn,
    environment,
    release,
    enabled: effectiveEnabled,
    sendDefaultPii,
    tracesSampleRate,
    replaysOnErrorSampleRate,
    replaysSessionSampleRate,
    integrations,
    initialScope: {
      tags: { app },
    },
    beforeSend(event, hint) {
      const message = event.exception?.values?.[0]?.value ?? "";
      if (shouldDropEvent(hint?.originalException, message)) return null;
      const scrubbed = scrub ? (scrub(event) as typeof event) : event;
      if (userBeforeSend) {
        const result = userBeforeSend(scrubbed);
        return (result as typeof event) ?? null;
      }
      return scrubbed;
    },
  });
}

export { Sentry };
